// 文章编辑器 ARTcr(/article/create)+ ARTed(/article/:id/edit;🔒 Authed)。
// 同构:同一编辑器,差别仅初始数据 + 提交端点(无 id → create;带 id → update)。
//   - 标题(1-120)+ 封面(自家 image,v_file_info_mine)+ 单分类(v_category_public)
//   - Tiptap 编辑器 + 插入文件/包面板(只写 uuid)
//   - 存草稿(status=draft)/ 发布(status=published)— CfxQI:操作按钮在顶栏右侧
// 契约源:docs/ui/user-page-flows/article.md ARTcr/ARTed + docs/api-list/user/articles.md §4。
// 设计稿:ui/dogdisk-user.pen CfxQI(Header 操作区 + coverRow + categoryRow + Toolbar)。
// 2026-06-05 module spec:发布必选分类(status=published 时 categoryId 为空则拦截);
//   已发布文章编辑锁分类(isPublished=true 时 categoryRow 禁点 + 不回传 category_id);
//   CategoryPickerPanel 锁 module="forum"(约爱上线后由入口参数化)。

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BackButton } from '../components/back-button'
import type { JSONContent } from '@tiptap/core'
import { ApiClientError, cn } from '@dogdisk/shared'
import { MobileScreen } from '../components/mobile-screen'
import { ArticleEditor, useArticleEditor } from '../components/article-editor/editor'
import { isEmptyDoc } from '../components/article-editor/embed-utils'
import { InsertFilePanel } from '../components/article-editor/insert-file-panel'
import { InsertPackPanel } from '../components/article-editor/insert-pack-panel'
import { CoverPicker, type CoverPick } from '../components/modals/cover-picker'
import { CategoryPickerPanel } from '../components/article-editor/category-picker-panel'
import { usePublicCategories } from '../_lib/discover-api'
import { useCreateArticle, useUpdateArticle, useMyArticle } from '../_lib/article-api'
import { resourceUrl } from '../_lib/resource-node-pref'

const TITLE_MAX = 120

/** content 抽纯文本摘要(≤300),供 excerpt。 */
function deriveExcerpt(json: JSONContent | null): string {
  if (!json) return ''
  const out: string[] = []
  const walk = (n: JSONContent) => {
    if (n.type === 'text' && n.text) out.push(n.text)
    ;(n.content ?? []).forEach(walk)
  }
  walk(json)
  return out.join(' ').trim().slice(0, 300)
}

export function ArticleEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const cats = usePublicCategories()
  const createMut = useCreateArticle()
  const updateMut = useUpdateArticle()
  const existing = useMyArticle(id)

  // 2026-06-05 module spec:已发布文章分类锁定(服务端 CATEGORY_LOCKED 双保险)
  const isPublished = isEdit && existing.data?.status === 'published'

  const [title, setTitle] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [cover, setCover] = useState<CoverPick | null>(null)
  const [content, setContent] = useState<JSONContent | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showFilePanel, setShowFilePanel] = useState(false)
  const [showPackPanel, setShowPackPanel] = useState(false)
  const [showCoverPanel, setShowCoverPanel] = useState(false)
  const [showCategoryPanel, setShowCategoryPanel] = useState(false)
  const [loadedId, setLoadedId] = useState<string | null>(null)

  const editor = useArticleEditor({ content, onUpdate: (json) => setContent(json) })

  // 分类 id → 名称(L1 + L2 铺平),供 categoryRow 回显选中名。
  const catNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const l1 of cats.data ?? []) {
      m.set(l1.id, l1.name)
      for (const c of l1.children) m.set(c.id, c.name)
    }
    return m
  }, [cats.data])

  // 编辑态:经 v_article_mine 按 id 预填(含草稿正文)。只填充一次。
  useEffect(() => {
    if (!isEdit || !existing.data || loadedId === existing.data.id) return
    setTitle(existing.data.title)
    setCategoryId(existing.data.category_id)
    if (existing.data.cover_file_info_id) {
      // 预填只拿到 cover_file_info_id(无 thumb);缩略图待重新选择时刷新。
      setCover({ id: existing.data.cover_file_info_id, thumbUrl: null, name: '' })
    }
    if (existing.data.content && editor) {
      editor.commands.setContent(existing.data.content)
      setContent(existing.data.content)
    }
    setLoadedId(existing.data.id)
  }, [isEdit, existing.data, editor, loadedId])

  const trimmedTitle = title.trim()
  const titleValid = trimmedTitle.length >= 1 && trimmedTitle.length <= TITLE_MAX
  const contentEmpty = isEmptyDoc(content)
  const busy = createMut.isPending || updateMut.isPending
  const categoryName = categoryId ? (catNameById.get(categoryId) ?? '已选分类') : null

  const submit = async (status: 'draft' | 'published') => {
    setError(null)
    if (!titleValid) {
      setError('标题须为 1-120 字')
      return
    }
    if (contentEmpty) {
      setError('正文不能为空')
      return
    }
    // 已发布文章(isPublished)分类已锁定且 body 不回传 category_id —— 不拦,服务端保持原分类
    // (存量无分类已发布文章:作者编辑标题保存不被卡;补分类走 owner SQL)。
    // 2026-06-10 spec D4:创建/存草稿也必选分类(后端 create 已 400 拦,分类决定发文 gate 的 module)。
    if (!categoryId && !isPublished) {
      setError(status === 'published' ? '发布必须选择分类' : '请先选择分类')
      setShowCategoryPanel(true)
      return
    }
    const body: {
      title: string
      content: JSONContent
      excerpt: string
      cover_file_info_id: string | null
      category_id?: string | null
      status: 'draft' | 'published'
    } = {
      title: trimmedTitle,
      content: content as JSONContent,
      excerpt: deriveExcerpt(content),
      cover_file_info_id: cover?.id ?? null,
      ...(isPublished ? {} : { category_id: categoryId }),
      status,
    }
    try {
      const res = isEdit
        ? await updateMut.mutateAsync({ id: id!, ...body })
        : await createMut.mutateAsync(body)
      if (status === 'published') navigate(`/article/${res.id}`)
      else navigate('/me/articles')
    } catch (e) {
      if (e instanceof ApiClientError) setError(e.message)
      else setError(e instanceof Error ? e.message : '保存失败')
    }
  }

  return (
    <MobileScreen className="h-dvh" noScroll>
      {/* Header(nEDWC)— back + 标题 + 操作区(存草稿 / 发布) */}
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-[#F3F4F6] px-4 pt-[max(10px,env(safe-area-inset-top))] pb-2">
        <div className="flex min-w-0 items-center gap-2">
          <BackButton className="shrink-0" />
          <h1 className="truncate text-[16px] font-semibold text-[#1A1A1A]">{isEdit ? '编辑文章' : '写文章'}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            data-testid="ARTcr-save-draft"
            onClick={() => submit('draft')}
            disabled={busy}
            className="rounded-[10px] bg-[#F6F7F8] px-3 py-2 text-[13px] font-medium text-[#6B7280] active:bg-[#EDEEF0] disabled:opacity-50"
          >
            存草稿
          </button>
          <button
            type="button"
            data-testid="ARTcr-publish"
            onClick={() => submit('published')}
            disabled={busy}
            className="rounded-[10px] bg-[#19caad] px-3.5 py-2 text-[13px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            {busy ? '保存中…' : '发布'}
          </button>
        </div>
      </header>

      <main data-testid={isEdit ? 'ARTed' : 'ARTcr'} className="flex min-h-0 flex-1 flex-col">
        {/* FormArea(Q2bxd)— 标题 + 封面 + 分类 */}
        <div className="flex shrink-0 flex-col gap-[14px] px-4 pt-4 pb-2">
          <input
            data-testid="ARTcr-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX + 10}
            placeholder="输入标题…"
            className="w-full border-b border-[#F3F4F6] py-2.5 text-[22px] font-semibold outline-none placeholder:text-[#D1D5DB]"
          />

          {/* coverRow(BLChq)— 从我的文件选 image 作封面 */}
          <button
            type="button"
            data-testid="ARTcr-cover"
            onClick={() => setShowCoverPanel(true)}
            className="flex items-center gap-3 text-left"
          >
            <span className="flex h-[60px] w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F6F7F8]">
              {cover?.thumbUrl ? (
                <img src={resourceUrl(cover.thumbUrl)} alt="" className="h-full w-full object-cover" />
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="9" cy="9" r="2" />
                  <path d="m21 15-4.5-4.5L5 21" />
                </svg>
              )}
            </span>
            <span className="flex flex-col gap-1">
              <span className="text-[13px] font-medium text-[#1A1A1A]">
                {cover ? '更换封面' : '添加封面（可选）'}
              </span>
              <span className="text-[11px] text-[#9CA3AF]">从『我的文件』选择 image</span>
            </span>
          </button>

          {/* categoryRow(Rtlmc)— v_category_public 单选 */}
          <button
            type="button"
            data-testid="ARTcr-category"
            disabled={isPublished}
            onClick={() => setShowCategoryPanel(true)}
            className={cn(
              'flex items-center gap-2 rounded-[10px] bg-[#F6F7F8] px-3 py-2.5',
              isPublished ? 'opacity-60' : 'active:bg-[#EDEEF0]',
            )}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
            </svg>
            <span className={cn('flex-1 text-left text-[13px]', categoryName ? 'text-[#1A1A1A]' : 'text-[#6B7280]')}>
              {categoryName ?? '选择分类'}
            </span>
            {isPublished && <span className="text-[11px] text-[#9CA3AF]">已发布不可改</span>}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>

        {error && <p className="shrink-0 px-4 pb-1 text-[13px] text-[#FF6B6B]">{error}</p>}

        {/* Toolbar(jRwg9)+ EditorArea(VpBIi)— ArticleEditor 自带工具栏 + 滚动内容区 */}
        <ArticleEditor
          editor={editor}
          onInsertFile={() => setShowFilePanel(true)}
          onInsertPack={() => setShowPackPanel(true)}
        />
      </main>

      <CoverPicker
        open={showCoverPanel}
        onClose={() => setShowCoverPanel(false)}
        currentCoverId={cover?.id ?? null}
        onSelect={(pick) => setCover(pick)}
        onClear={() => setCover(null)}
      />
      {/* 本期编辑器仅论坛模块;约爱上线后由入口参数化 */}
      <CategoryPickerPanel
        open={showCategoryPanel}
        selectedId={categoryId}
        module="forum"
        onClose={() => setShowCategoryPanel(false)}
        onSelect={(cid) => setCategoryId(cid)}
      />
      <InsertFilePanel
        open={showFilePanel}
        onClose={() => setShowFilePanel(false)}
        onConfirm={(fileIds) => {
          if (!editor) return
          editor.commands.insertFileRefs(fileIds)
        }}
      />
      <InsertPackPanel
        open={showPackPanel}
        onClose={() => setShowPackPanel(false)}
        onConfirm={(packIds) => {
          if (!editor) return
          editor.commands.insertPackRefs(packIds)
        }}
      />
    </MobileScreen>
  )
}
